import { z } from 'zod';
import { DOCUMENT_FILE_MAX_BYTES } from './userDocument.js';
// 1:1 chat between a registered user and the DriverForce admin team.
// Wire-level constants and validation schemas live here so the client,
// admin, and api share one source of truth (see CLAUDE.md).
export const CHAT_SENDER_TYPES = ['user', 'admin', 'system'];
export const CHAT_MESSAGE_BODY_MAX = 4000;
export const CHAT_ATTACHMENT_MAX_BYTES = DOCUMENT_FILE_MAX_BYTES;
export const CHAT_ATTACHMENTS_PER_MESSAGE_MAX = 5;
export const CHAT_HISTORY_PAGE_DEFAULT = 50;
export const CHAT_HISTORY_PAGE_MAX = 50;
export const CHAT_HISTORY_GAP_REPLAY_MAX = 200;
export const CHAT_ATTACHMENT_MIME_ALLOWLIST = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
];
// Allowlist of system message i18n keys the API is permitted to emit.
// The client renders `t(<key>)` so an arbitrary key would be a stored-XSS
// vector through translation files. Keep this set tiny and reviewed.
export const CHAT_SYSTEM_I18N_KEYS = ['chat.welcome.driverforce_team'];
export const CHAT_WELCOME_I18N_KEY = 'chat.welcome.driverforce_team';
// --------------------------------------------------------------------------
// Request schemas
// --------------------------------------------------------------------------
export const postChatMessageSchema = z
    .object({
    body: z
        .string()
        .trim()
        .min(1)
        .max(CHAT_MESSAGE_BODY_MAX)
        .optional(),
    clientNonce: z.string().min(8).max(64),
    attachmentIds: z
        .array(z.string().uuid())
        .max(CHAT_ATTACHMENTS_PER_MESSAGE_MAX)
        .optional(),
})
    .refine((v) => (v.body && v.body.length > 0) || (v.attachmentIds && v.attachmentIds.length > 0), { message: 'body or attachmentIds required', path: ['body'] });
export const markChatReadSchema = z.object({
    upToMessageId: z.string().uuid(),
});
export const listChatMessagesQuerySchema = z
    .object({
    before: z.string().uuid().optional(),
    after: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(CHAT_HISTORY_PAGE_MAX).optional(),
})
    .refine((v) => !(v.before && v.after), { message: 'before XOR after' });
export const listAdminConversationsQuerySchema = z.object({
    search: z.string().trim().min(1).max(120).optional(),
    filter: z.enum(['all', 'unread']).optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(50).optional(),
});
export const adminOpenConversationSchema = z.object({
    userId: z.string().uuid(),
});
// --------------------------------------------------------------------------
// WebSocket frames (Zod-validated; server validates inbound, client mostly
// trusts outbound but the discriminated union still gives type safety).
// --------------------------------------------------------------------------
export const chatWsClientFrameSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ping') }),
    z.object({
        type: z.literal('subscribe.ack'),
        lastSeenMessageId: z.string().uuid().optional(),
    }),
]);
//# sourceMappingURL=chat.js.map